import {
  buildASOPath,
  buildWebPath,
  CLOCKFACE_PATHS,
  useFeatureFlag,
  useNeonPath,
} from "lib/resource-finder";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsOnboardingCheck } from "./useIsOnboardingCheck";
import { useGetMemberPermissionsQuery } from "types/generated/operations";
import { NeonSkyFeatureFlag } from "lib/neon-sky/neon-sky-provider";

export interface BankFormData {
  accountNumber: string;
  routingNumber: string;
  bankName: string;
}

export interface BankFormContextValue {
  formData: BankFormData;
  setFormData: React.Dispatch<React.SetStateAction<BankFormData>>;
  showGlobalNetworkError?: boolean;
  setShowGlobalNetworkError?: (value: boolean) => void;
  isOnboarding: boolean;
  isLoadingOnboardingCheck: boolean;
  isOnReverseWire?: boolean | null;
  isOnPlaidTwoDay?: boolean | null;
  isOnPlaidFourDay?: boolean | null;
  returnToFormPage: () => void;
  backRoute?: string;
  acceptedTerms: boolean;
  acceptTerms: () => void;
}

const initialData: BankFormData = {
  accountNumber: "",
  routingNumber: "",
  bankName: "",
};

export const defaultBankFormContext: BankFormContextValue = {
  formData: initialData,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFormData: () => {},
  showGlobalNetworkError: false,
  isOnboarding: false,
  isLoadingOnboardingCheck: true,
  returnToFormPage: () => {},
  acceptedTerms: false,
  acceptTerms: () => {},
};

export const BankFormContext = createContext<BankFormContextValue>(
  defaultBankFormContext
);

export const useBankForm = () => useContext(BankFormContext);

interface BankFormProviderProps {
  children: ReactNode;
}

export const BankFormProvider = ({ children }: BankFormProviderProps) => {
  const { isLoading, data } = useIsOnboardingCheck();
  const [formData, setFormData] = useState(initialData);
  const [acceptedTerms, setAcceptTerms] = useState(false);
  const [showGlobalNetworkError, setShowGlobalNetworkError] =
    useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { data: permissionsData } = useGetMemberPermissionsQuery();
  const cdmsCanonicalCompanyId =
    permissionsData?.authenticatedMember?.company?.cdmsCanonicalCompanyId || "";
  const billingPath = buildASOPath(
    `/${cdmsCanonicalCompanyId}/settings/billing`
  );
  const isCompanySettingsFlagEnabled = useFeatureFlag({
    flag: NeonSkyFeatureFlag.COMPANY_SETTINGS,
  });

  useEffect(() => {
    setShowGlobalNetworkError(false);
  }, [location.pathname]);

  const returnToFormPage = useCallback(() => {
    navigate("/company-settings/edit-bank-account", {
      state: location.state,
    });
    window.scrollTo(0, 0);
  }, [navigate, location.state]);

  let backRoute = useNeonPath(CLOCKFACE_PATHS.COMPANY);

  if (data?.is_onboarding) {
    backRoute = buildWebPath("/onboarding_checklist");
  } else if (isCompanySettingsFlagEnabled) {
    backRoute = billingPath;
  }

  const acceptTerms = () => {
    returnToFormPage();
    setAcceptTerms(true);
  };

  const value = {
    formData,
    setFormData,
    showGlobalNetworkError,
    setShowGlobalNetworkError,
    isOnboarding: data?.is_onboarding ?? false,
    isOnReverseWire: data?.is_on_reverse_wire ?? false,
    isOnPlaidTwoDay: data?.is_on_plaid_two_day ?? false,
    isOnPlaidFourDay: data?.is_on_plaid_four_day ?? false,
    isLoadingOnboardingCheck: isLoading,
    backRoute,
    returnToFormPage,
    acceptedTerms,
    acceptTerms,
  };

  return (
    <BankFormContext.Provider value={value}>
      {children}
    </BankFormContext.Provider>
  );
};
